import React, { useState, useCallback } from 'react';
import { ActivityPresetDefinition, ActivityPresetPreviewRequest, PresetRuleDescriptor, FieldDefinition, ActivityPresetPreviewResponse, SeriesPresetDefinition, SeriesPresetPreviewRequest, SeriesPresetPreviewResponse } from '../../types/presets';
import { ActivityType, ActivityScoreRuleRequest } from '../../types/activity';
import { ScoreType } from '../../types/activity';
import PresetRuleCard from './PresetRuleCard';
import PresetSelector from './PresetSelector';
import {
    getCodeLabel,
    getPresetDisplayName,
    localizeNotes,
    localizeVi,
} from '../../utils/vietnameseLabels';

type PresetDefinition = ActivityPresetDefinition | SeriesPresetDefinition;
type PreviewResponse = ActivityPresetPreviewResponse | SeriesPresetPreviewResponse;

interface PresetConfigPanelProps {
    presets: PresetDefinition[];
    selectedPresetCode: string;
    onPresetChange: (code: string) => void;
    config: Record<string, unknown>;
    onConfigChange: (config: Record<string, unknown>) => void;
    enabledRules: Record<string, boolean>;
    onRuleToggle: (ruleKey: string, enabled: boolean) => void;
    onPreview: () => Promise<void>;
    previewResponse: PreviewResponse | null;
    previewLoading: boolean;
    mode: 'activity' | 'series';
    activityType?: ActivityType;
    requiresSubmission?: boolean | null;
    errors?: Record<string, string>;
    externalOptions?: Record<string, Array<{ value: string | number; label: string }>>;
    /** P6-10: lock preset dropdown khi edit activity (giữ value, vẫn cho edit presetConfig). */
    lockPreset?: boolean;
}

const buildEnabledRules = (preset: PresetDefinition): Record<string, boolean> => {
    const rules: Record<string, boolean> = {};
    for (const rule of preset.supportedRules) {
        rules[rule.ruleKey] = rule.required ? true : rule.enabledByDefault;
    }
    return rules;
};

const buildInitialConfig = (preset: PresetDefinition): Record<string, unknown> => {
    const config: Record<string, unknown> = {};
    for (const rule of preset.supportedRules) {
        for (const field of rule.fieldDefinitions) {
            if (field.defaultValue !== undefined && field.defaultValue !== null) {
                config[field.fieldName] = field.defaultValue;
            }
        }
    }
    return config;
};

const PresetConfigPanel: React.FC<PresetConfigPanelProps> = ({
    presets,
    selectedPresetCode,
    onPresetChange,
    config,
    onConfigChange,
    enabledRules,
    onRuleToggle,
    onPreview,
    previewResponse,
    previewLoading,
    mode,
    activityType,
    requiresSubmission,
    errors = {},
    externalOptions,
    lockPreset = false
}) => {
    const selectedPreset = presets.find(p => p.code === selectedPresetCode);

    const handlePresetChange = useCallback((code: string) => {
        onPresetChange(code);
        if (code) {
            const preset = presets.find(p => p.code === code);
            if (preset) {
                const newRules = buildEnabledRules(preset);
                const newConfig = buildInitialConfig(preset);
                // Merge existing onRuleToggle and onConfigChange calls
                for (const [key, enabled] of Object.entries(newRules)) {
                    onRuleToggle(key, enabled);
                }
                onConfigChange(newConfig);
            }
        }
    }, [presets, onPresetChange, onRuleToggle, onConfigChange]);

    const handleRuleToggle = (ruleKey: string, enabled: boolean) => {
        onRuleToggle(ruleKey, enabled);
    };

    const handleFieldChange = (fieldName: string, value: unknown) => {
        onConfigChange({
            ...config,
            [fieldName]: value
        });
    };

    const isActivityPreview = (res: PreviewResponse): res is ActivityPresetPreviewResponse => {
        return 'activityType' in res;
    };

    const isSeriesPreview = (res: PreviewResponse): res is SeriesPresetPreviewResponse => {
        return 'milestonePoints' in res && !('activityType' in res);
    };

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-base font-semibold tracking-tight text-primary-900">
                    Mẫu cấu hình điểm
                </h3>
                <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                    Chọn mẫu có sẵn để hệ thống sinh luật điểm, hoặc tùy chỉnh thủ công.
                </p>
            </div>

            <PresetSelector
                presets={presets}
                selectedCode={selectedPresetCode}
                onChange={handlePresetChange}
                disabled={lockPreset}
                label={mode === 'series' ? 'Mẫu cấu hình chuỗi sự kiện' : 'Mẫu cấu hình sự kiện'}
                placeholder="— Tùy chỉnh (không dùng mẫu) —"
            />
            {lockPreset && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 leading-relaxed">
                    Mẫu cấu hình đã khóa khi chỉnh sửa. Bạn vẫn có thể tùy chỉnh chi tiết bên dưới.
                </p>
            )}

            {selectedPreset && (
                <div className="space-y-4">
                    {selectedPreset.supportedRules.map(rule => {
                        const isEnabled = enabledRules[rule.ruleKey] ?? rule.enabledByDefault;
                        return (
                            <PresetRuleCard
                                key={rule.ruleKey}
                                rule={rule}
                                enabled={isEnabled}
                                onToggle={handleRuleToggle}
                                fieldValues={config}
                                onFieldChange={handleFieldChange}
                                errors={errors}
                                externalOptions={externalOptions}
                                presetCode={selectedPreset.code}
                                // P6.1: TASK_OVERDUE chỉ bật được khi SUBMISSION_GRADED bật.
                                // Chỉ disable toggle khi đang OFF (không cho bật); nếu đang ON vẫn cho tắt.
                                toggleDisabled={rule.ruleKey === 'TASK_OVERDUE' && !enabledRules.SUBMISSION_GRADED && !isEnabled}
                            />
                        );
                    })}

                    <button
                        type="button"
                        onClick={onPreview}
                        disabled={previewLoading}
                        className="w-full rounded-xl bg-primary-900 px-4 py-2.5 text-sm font-medium text-white transition-all hover:bg-primary-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-900"
                    >
                        {previewLoading ? 'Đang xem trước...' : 'Xem trước cấu hình luật điểm'}
                    </button>

                    {previewResponse && isActivityPreview(previewResponse) && (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-emerald-900">
                                Xem trước: {getPresetDisplayName(previewResponse.presetCode)}
                            </h4>
                            {(() => {
                                // P6.1: ưu tiên previewRows (display-ready), fallback scoreRules.
                                const rows = previewResponse.previewRows;
                                if (rows && rows.length > 0) {
                                    return (
                                        <div className="overflow-x-auto rounded-lg border border-emerald-200/80">
                                            <table className="min-w-full text-xs text-left">
                                                <thead className="text-emerald-800 bg-emerald-100/80">
                                                    <tr>
                                                        <th className="px-2.5 py-2 font-semibold">Tình huống</th>
                                                        <th className="px-2.5 py-2 font-semibold">Loại điểm</th>
                                                        <th className="px-2.5 py-2 font-semibold text-right">Điểm</th>
                                                        <th className="px-2.5 py-2 font-semibold">Đối tượng</th>
                                                        <th className="px-2.5 py-2 font-semibold">Học kỳ</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map((row, idx) => (
                                                        <tr key={idx} className="border-t border-emerald-200/70">
                                                            <td className="px-2.5 py-2 text-emerald-900">
                                                                {localizeVi(row.description) ||
                                                                    getCodeLabel(row.scenario) ||
                                                                    getCodeLabel(row.triggerType)}
                                                            </td>
                                                            <td className="px-2.5 py-2 text-emerald-800">
                                                                {getCodeLabel(row.scoreType, localizeVi(row.scoreType))}
                                                            </td>
                                                            <td className={`px-2.5 py-2 text-right font-semibold tabular-nums ${row.points >= 0 ? 'text-emerald-800' : 'text-rose-600'}`}>
                                                                {row.points >= 0 ? `+${row.points}` : row.points}
                                                            </td>
                                                            <td className="px-2.5 py-2 text-gray-600">
                                                                {getCodeLabel(row.audience, localizeVi(row.audience))}
                                                            </td>
                                                            <td className="px-2.5 py-2 text-gray-600">
                                                                {getCodeLabel(row.semester, localizeVi(row.semester))}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                }
                                // fallback cũ: render từ scoreRules
                                const hasAnyFailPoints = previewResponse.scoreRules.some(
                                    (r: ActivityScoreRuleRequest) => r.failPoints !== null && r.failPoints !== undefined
                                );
                                return (
                                    <div className="overflow-x-auto rounded-lg border border-emerald-200/80">
                                        <table className="min-w-full text-xs text-left">
                                            <thead className="text-emerald-800 bg-emerald-100/80">
                                                <tr>
                                                    <th className="px-2.5 py-2 font-semibold">Kích hoạt</th>
                                                    <th className="px-2.5 py-2 font-semibold">Loại điểm</th>
                                                    <th className="px-2.5 py-2 font-semibold text-right">Điểm</th>
                                                    {hasAnyFailPoints && (
                                                        <th className="px-2.5 py-2 font-semibold text-right">Điểm trừ</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewResponse.scoreRules.map((rule: ActivityScoreRuleRequest, idx: number) => (
                                                    <tr key={idx} className="border-t border-emerald-200/70">
                                                        <td className="px-2.5 py-2 text-emerald-900">
                                                            {getCodeLabel(rule.triggerType)}
                                                        </td>
                                                        <td className="px-2.5 py-2 text-emerald-800">
                                                            {getCodeLabel(rule.scoreType)}
                                                        </td>
                                                        <td className="px-2.5 py-2 text-right text-emerald-800 font-semibold tabular-nums">{rule.points}</td>
                                                        {hasAnyFailPoints && (
                                                            <td className="px-2.5 py-2 text-right text-rose-600 tabular-nums">
                                                                {rule.failPoints != null ? rule.failPoints : '—'}
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}
                            {previewResponse.notes.length > 0 && (
                                <ul className="list-disc list-inside text-xs text-emerald-800 space-y-0.5">
                                    {localizeNotes(previewResponse.notes).map((note, idx) => (
                                        <li key={idx}>{note}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {previewResponse && isSeriesPreview(previewResponse) && (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-emerald-900">
                                Xem trước: {getPresetDisplayName(previewResponse.presetCode)}
                            </h4>
                            <div className="text-sm text-emerald-800 space-y-1">
                                <p>Loại điểm: {getCodeLabel(previewResponse.scoreType)}</p>
                                <p>Mốc điểm:</p>
                                <div className="ml-1 space-y-1">
                                    {Object.entries(previewResponse.milestonePoints).map(([count, points]) => (
                                        <div key={count} className="flex items-center gap-2 tabular-nums">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                            <span>{count} sự kiện → {points} điểm</span>
                                        </div>
                                    ))}
                                </div>
                                {previewResponse.minimumRequirementEnabled && (
                                    <p className="mt-2 text-amber-800">
                                        Yêu cầu tối thiểu: {previewResponse.minimumRequiredEvents} sự kiện, phạt {previewResponse.minimumPenaltyPoints} điểm
                                    </p>
                                )}
                            </div>
                            {previewResponse.notes.length > 0 && (
                                <ul className="list-disc list-inside text-xs text-emerald-800">
                                    {localizeNotes(previewResponse.notes).map((note, idx) => (
                                        <li key={idx}>{note}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PresetConfigPanel;
